import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import type { TableColumnDefinition, SortDirection, TableColumnId } from '@fluentui/react-components';
import { WidgetContainer } from '../WidgetContainer';
import type { RoomDetail, RoomTypeConfig, HealthStatus } from '@/types/reports';
import { useRoomDetailTableStyles } from './RoomDetailTable.styles';

export interface RoomDetailTableProps {
  data: RoomDetail[];
  roomTypes: RoomTypeConfig[];
}

interface RoomRow extends RoomDetail {
  healthStatus: HealthStatus;
}

function getHealthStatus(utilization: number): HealthStatus {
  if (utilization >= 80) return 'constrained';
  if (utilization >= 40) return 'healthy';
  return 'underutilized';
}

export const RoomDetailTable = ({ data, roomTypes }: RoomDetailTableProps) => {
  const styles = useRoomDetailTableStyles();
  const { t } = useTranslation('reports');

  const [sortState, setSortState] = useState<{ sortColumn: TableColumnId; sortDirection: SortDirection }>({
    sortColumn: 'utilization',
    sortDirection: 'descending',
  });

  const colorMap = useMemo(() => new Map(roomTypes.map((rt) => [rt.name, rt.color])), [roomTypes]);

  const rows: RoomRow[] = useMemo(
    () => data.map((r) => ({ ...r, healthStatus: getHealthStatus(r.utilization) })),
    [data]
  );

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    const dir = sortState.sortDirection === 'ascending' ? 1 : -1;
    sorted.sort((a, b) => {
      switch (sortState.sortColumn) {
        case 'name': return dir * a.name.localeCompare(b.name);
        case 'roomType': return dir * a.roomType.localeCompare(b.roomType);
        case 'utilization': return dir * (a.utilization - b.utilization);
        case 'bookings': return dir * (a.bookings - b.bookings);
        case 'avgDuration': return dir * (a.avgDuration - b.avgDuration);
        case 'status': return dir * a.healthStatus.localeCompare(b.healthStatus);
        default: return 0;
      }
    });
    return sorted;
  }, [rows, sortState]);

  const handleSortChange = useCallback((_: unknown, next: { sortColumn: TableColumnId | undefined; sortDirection: SortDirection }) => {
    if (next.sortColumn) {
      setSortState({ sortColumn: next.sortColumn, sortDirection: next.sortDirection });
    }
  }, []);

  const columns: TableColumnDefinition<RoomRow>[] = useMemo(() => {
    const statusBadgeClass = (status: HealthStatus) => {
      switch (status) {
        case 'constrained': return mergeClasses(styles.badge, styles.badgeConstrained);
        case 'healthy': return mergeClasses(styles.badge, styles.badgeHealthy);
        case 'underutilized': return mergeClasses(styles.badge, styles.badgeUnderutilized);
      }
    };

    const utilClass = (status: HealthStatus) => {
      switch (status) {
        case 'constrained': return styles.utilConstrained;
        case 'healthy': return styles.utilHealthy;
        case 'underutilized': return styles.utilUnderutilized;
      }
    };

    return [
    createTableColumn<RoomRow>({
      columnId: 'name',
      compare: (a, b) => a.name.localeCompare(b.name),
      renderHeaderCell: () => t('roomTable.room'),
      renderCell: (item) => <Text>{item.name}</Text>,
    }),
    createTableColumn<RoomRow>({
      columnId: 'roomType',
      compare: (a, b) => a.roomType.localeCompare(b.roomType),
      renderHeaderCell: () => t('roomTable.type'),
      renderCell: (item) => (
        <span className={styles.typeBadge} style={{ color: colorMap.get(item.roomType) }}>
          {item.roomType}
        </span>
      ),
    }),
    createTableColumn<RoomRow>({
      columnId: 'utilization',
      compare: (a, b) => a.utilization - b.utilization,
      renderHeaderCell: () => t('roomTable.utilPct'),
      renderCell: (item) => <Text className={utilClass(item.healthStatus)}>{item.utilization}%</Text>,
    }),
    createTableColumn<RoomRow>({
      columnId: 'bookings',
      compare: (a, b) => a.bookings - b.bookings,
      renderHeaderCell: () => t('roomTable.bookings'),
      renderCell: (item) => <Text>{item.bookings}</Text>,
    }),
    createTableColumn<RoomRow>({
      columnId: 'avgDuration',
      compare: (a, b) => a.avgDuration - b.avgDuration,
      renderHeaderCell: () => t('roomTable.avgDuration'),
      renderCell: (item) => <Text>{item.avgDuration} {t('roomTable.durationUnit')}</Text>,
    }),
    createTableColumn<RoomRow>({
      columnId: 'status',
      compare: (a, b) => a.healthStatus.localeCompare(b.healthStatus),
      renderHeaderCell: () => t('roomTable.status'),
      renderCell: (item) => (
        <span className={statusBadgeClass(item.healthStatus)}>
          {t(`roomTable.${item.healthStatus}`)}
        </span>
      ),
    }),
    ];
  }, [t, colorMap, styles]);

  return (
    <WidgetContainer title={t('widgets.roomDetailTableTitle')}>
      <div className={styles.root}>
        <DataGrid
          items={sortedRows}
          columns={columns}
          sortable
          sortState={sortState}
          onSortChange={handleSortChange}
          aria-label={t('widgets.roomDetailTableTitle')}
          getRowId={(item: RoomRow) => item.id}
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<RoomRow>>
            {({ item, rowId }) => (
              <DataGridRow<RoomRow> key={rowId}>
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
    </WidgetContainer>
  );
};

RoomDetailTable.displayName = 'RoomDetailTable';
